/* Audit trail — handover §14 step 11.

   §6.3: append-only in practice. Everyone gets Read, nobody gets Contribute,
   and all writes go through the Function app identity — so nobody can edit
   their own trail. That is a SharePoint permission, not a UI decision, which is
   why this screen has no delete button and never will: it would be lying about
   what it could do.

   Read-only, then, and the design question is only how to find things in it.
   Search across actor, action and object covers the two questions actually
   asked of an audit log — "what happened to this thing" and "what did this
   person do" — without a query builder nobody would use twice. */

import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { IAuditEntry } from '../../models';
import { qk } from '../../query/keys';
import { useServices } from '../../services/context';
import {
  Badge,
  Controls,
  EmptyState,
  LoadError,
  Panel,
  SearchBox,
  Segmented,
  TableSkeleton,
} from '../ui';
import s from './screens.module.scss';

/** The kind of thing an entry is, from its action verb. Enough to filter on,
    and it needs no schema change to work on entries already written. */
export function kindOf(entry: IAuditEntry): 'added' | 'changed' | 'removed' | 'other' {
  const act = entry.act.toLowerCase();
  /* The verb anywhere, not at the start.
   *
   *  Matching the start was why every filter but All came back empty: nearly
   *  every entry in this list is written by the sync, and the sync says "Sync
   *  created", "Sync updated" and "Sync differences" — so all three fell past
   *  three tests looking at the first word and landed in a category the filter
   *  did not offer. */
  /* Split into words rather than matched with a word-boundary regex. It
     reads the same and cannot be broken by an escape going missing, which
     is exactly what happened to the first attempt at this: the \b became a
     literal backspace, every branch stopped matching, and the source looked
     perfectly correct because a backspace is invisible in every view. */
  const words = act.split(/[^a-z]+/);
  const says = (...verbs: string[]) => verbs.some((v) => words.includes(v));

  if (says('added', 'created', 'imported', 'new')) return 'added';
  if (says('removed', 'deleted')) return 'removed';
  if (says('updated', 'saved', 'changed', 'edited')) return 'changed';
  return 'other';
}

const TONE = { added: 'ok', changed: 'info', removed: 'bad', other: 'muted' } as const;

export function AuditScreen() {
  const svc = useServices();
  const [query, setQuery] = useState('');
  const [lens, setLens] = useState('all');

  const audit = useQuery({ queryKey: qk.audit, queryFn: () => svc.audit.list(500) });

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (audit.data ?? []).filter((e) => {
      if (lens !== 'all' && kindOf(e) !== lens) return false;
      if (!q) return true;
      return (
        e.who.toLowerCase().includes(q) ||
        e.act.toLowerCase().includes(q) ||
        e.obj.toLowerCase().includes(q)
      );
    });
  }, [audit.data, query, lens]);

  return (
    <>
      <div className={s.head}>
        <div className={s.grow}>
          <h1>Audit trail</h1>
          <p>
            Every change, in order. Append-only — entries cannot be edited or deleted.
          </p>
        </div>
      </div>

      {audit.isError && <LoadError error={audit.error} what="the audit trail" />}

      <Controls count={`${rows.length} of ${audit.data?.length ?? 0}`}>
        <SearchBox value={query} onChange={setQuery} placeholder="Who, what, or which record…" />
        <Segmented
          label="Kind"
          value={lens}
          onChange={setLens}
          options={[
            { value: 'all', label: 'All' },
            { value: 'added', label: 'Added' },
            { value: 'changed', label: 'Changed' },
            { value: 'removed', label: 'Removed' },
            /* Reachable at last. "Sync differences" is a report rather than a
               change, so it belongs in its own bucket — but a bucket nothing
               can select is a bucket things disappear into. */
            { value: 'other', label: 'Reported' },
          ]}
        />
      </Controls>

      <Panel flush>
        {audit.isLoading ? (
          <TableSkeleton />
        ) : rows.length === 0 ? (
          <EmptyState icon="ti-history" title="Nothing recorded">
            {query || lens !== 'all'
              ? 'Nothing matches. Try clearing the filters.'
              : 'The trail fills as the registers are used.'}
          </EmptyState>
        ) : (
          <div className={s.rows}>
            {rows.map((e, i) => (
              <div key={`${e.when}-${i}`} className={s.row}>
                <span className={s.rowIcon}>
                  <i className={`ti ${e.icon || 'ti-history'}`} aria-hidden />
                </span>
                <div className={s.rowMain}>
                  <div className={s.rowTitle}>
                    {e.act} — {e.obj}
                  </div>
                  <div className={s.rowSub}>{e.who}</div>
                </div>
                <Badge tone={TONE[kindOf(e)]}>{kindOf(e)}</Badge>
                <div className={s.rowWhen}>{e.when}</div>
              </div>
            ))}
          </div>
        )}
      </Panel>
    </>
  );
}
